import React from "react";
import "./DateRangePicker.css";
import { IconCalendar } from "../../Common/EmcureReactIcons";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";

const emcureTimePickerTheme = createTheme({
  palette: {
    primary: { main: "#B90005", dark: "#8B0000" },
  },
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          justifyContent: "center",
          textAlign: "center",
          "&.Mui-selected": {
            backgroundColor: "rgba(185, 0, 5, 0.12) !important",
            "&:hover": {
              backgroundColor: "rgba(185, 0, 5, 0.2) !important",
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none" },
      },
    },
  },
});

const timeStringToDayjs = (timeStr) => {
  const [h = 0, m = 0, s = 0] = (timeStr || "00:00:00").split(":").map(Number);
  return dayjs().hour(h).minute(m).second(s).millisecond(0);
};
const dayjsToTimeString = (d) => (d && d.isValid() ? d.format("HH:mm:ss") : "00:00:00");

/**
 * Shared date range picker UI for Dashboard, Sessions, and Feedbacks.
 * Consume useDateRange() in the parent and pass the returned object as props.
 */
const DateRangePicker = ({
  getDateRangeLabel,
  isDatePickerOpen,
  openPicker,
  visibleMonth,
  calendarSelectionDays,
  setCalendarSelectionDays,
  setSelectedDays,
  pendingDateForTime,
  buildMonthMatrix,
  isDateInRange,
  isRangeBoundary,
  handleCalendarDayClick,
  handleApplyDateWithTime,
  handleQuickDateFilter,
  handlePrevMonth,
  handleNextMonth,
  selectedStartTime,
  setSelectedStartTime,
  selectedEndTime,
  setSelectedEndTime,
}) => {
  return (
    <div className="date-range-picker-root">
      <button
        type="button"
        className="date-range-toggle"
        onClick={openPicker}
      >
        <span className="date-range-icon" aria-hidden="true">
          <IconCalendar width={16} height={16} />
        </span>
        <span className="date-range-text">{getDateRangeLabel()}</span>
      </button>
      {isDatePickerOpen && (
        <div className="date-range-popover">
          <div className="date-range-popover-arrow" />
          <div className="date-range-popover-inner">
            <div className="date-range-popover-header">
              <button type="button" className="date-nav-btn" onClick={handlePrevMonth} aria-label="Previous month">
                ‹
              </button>
              <div className="date-range-month-titles">
                <span>
                  {visibleMonth.toLocaleString("en-US", { month: "short", year: "numeric" })}
                </span>
                <span>
                  {new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1).toLocaleString("en-US", {
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>
              <button type="button" className="date-nav-btn" onClick={handleNextMonth} aria-label="Next month">
                ›
              </button>
            </div>
            <div className="date-range-quick-filters">
              <button
                type="button"
                className={`quick-filter-btn ${calendarSelectionDays === 1 ? "active" : ""}`}
                onClick={() => {
                  setCalendarSelectionDays(1);
                  setSelectedDays(1);
                }}
                aria-pressed={calendarSelectionDays === 1}
              >
                1 Day
              </button>
              <button
                type="button"
                className={`quick-filter-btn ${calendarSelectionDays === 7 ? "active" : ""}`}
                onClick={() => {
                  setCalendarSelectionDays(7);
                  setSelectedDays(7);
                }}
                aria-pressed={calendarSelectionDays === 7}
              >
                7 Days
              </button>
              <button
                type="button"
                className={`quick-filter-btn ${calendarSelectionDays === 30 ? "active" : ""}`}
                onClick={() => handleQuickDateFilter(30)}
                aria-pressed={calendarSelectionDays === 30}
              >
                1 Month
              </button>
            </div>
            {calendarSelectionDays === 1 && (
              <p className="date-picker-hint">Select a date below</p>
            )}
            {calendarSelectionDays === 7 && (
              <p className="date-picker-hint">Select end date below (7 days ending on selected date)</p>
            )}
            {pendingDateForTime && (
              <div className="time-picker-section">
                <p className="time-picker-hint">
                  {calendarSelectionDays === 7 ? "End date: " : "Selected: "}
                  {new Date(pendingDateForTime + "T12:00:00").toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                  {calendarSelectionDays === 7 && " (7-day range)"}
                </p>
                <ThemeProvider theme={emcureTimePickerTheme}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="time-picker-row">
                      <div className="time-picker-field">
                        <TimePicker
                          label="Start time"
                          ampm={false}
                          value={timeStringToDayjs(selectedStartTime)}
                          onChange={(newValue) => setSelectedStartTime(dayjsToTimeString(newValue))}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": { borderColor: "#e5e7eb" },
                                  "&:hover fieldset": { borderColor: "#B90005" },
                                  "&.Mui-focused fieldset": { borderColor: "#B90005", borderWidth: 2 },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#B90005" },
                              },
                            },
                            digitalClockSectionItem: {
                              sx: { justifyContent: "center", textAlign: "center" },
                            },
                          }}
                        />
                      </div>
                      <div className="time-picker-field">
                        <TimePicker
                          label="End time"
                          ampm={false}
                          value={timeStringToDayjs(selectedEndTime)}
                          onChange={(newValue) => setSelectedEndTime(dayjsToTimeString(newValue))}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": { borderColor: "#e5e7eb" },
                                  "&:hover fieldset": { borderColor: "#B90005" },
                                  "&.Mui-focused fieldset": { borderColor: "#B90005", borderWidth: 2 },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#B90005" },
                              },
                            },
                            digitalClockSectionItem: {
                              sx: { justifyContent: "center", textAlign: "center" },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </LocalizationProvider>
                </ThemeProvider>
                <button type="button" className="time-picker-apply-btn" onClick={handleApplyDateWithTime}>
                  Apply
                </button>
              </div>
            )}
            <div className="date-range-calendars">
              {[0, 1].map((offset) => {
                const monthDate = new Date(
                  visibleMonth.getFullYear(),
                  visibleMonth.getMonth() + offset,
                  1
                );
                const matrix = buildMonthMatrix(monthDate);
                const monthLabel = monthDate.toLocaleString("en-US", {
                  month: "short",
                  year: "numeric",
                });
                return (
                  <div className="date-calendar" key={monthLabel}>
                    <div className="date-calendar-title">{monthLabel}</div>
                    <div className="date-calendar-grid">
                      <div className="date-calendar-weekdays">
                        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                          <span key={d}>{d}</span>
                        ))}
                      </div>
                      <div className="date-calendar-weeks">
                        {matrix.map((week, wi) => (
                          <div className="date-calendar-week" key={wi}>
                            {week.map((day, di) => {
                              if (!day) {
                                return (
                                  <button
                                    key={`${wi}-${di}`}
                                    type="button"
                                    className="date-cell empty"
                                    tabIndex={-1}
                                  />
                                );
                              }
                              const inRange = isDateInRange(day);
                              const isBoundary = isRangeBoundary(day);
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const dCopy = new Date(day);
                              dCopy.setHours(0, 0, 0, 0);
                              const isFuture = dCopy > today;
                              const cellClassNames = [
                                "date-cell",
                                inRange ? "in-range" : "",
                                isBoundary ? "range-boundary" : "",
                                isFuture ? "disabled" : "",
                              ]
                                .filter(Boolean)
                                .join(" ");
                              return (
                                <button
                                  key={day.toISOString()}
                                  type="button"
                                  className={cellClassNames}
                                  onClick={() => !isFuture && handleCalendarDayClick(day)}
                                >
                                  {day.getDate()}
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
